import Box from '@mui/material/Box';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import IcoDrag from 'components/icon/icons/IcoDrag';
import { COLORS } from 'muiTheme/colors';
import { useClearBlockSelection } from '../GridDndEditor/useClearBlockSelection';
import { SectionGhost } from './SectionGhost';
import SectionMenu from './SectionMenu';
import type { OnReorderFn } from './types';
import { useScrollToSection } from './useScrollToSection';
import { useSectionDragAndDrop, type UseSectionDragAndDropProps } from './useSectionDragAndDrop';

type SectionProps = {
  id: string;
  index: number;
  title: string;
  isActive: boolean;
  onReorder: OnReorderFn;
  draggedRef: UseSectionDragAndDropProps['draggedRef'];
  isLast: boolean;
};

export default function Section({ id, index, title, isActive, onReorder, draggedRef }: SectionProps) {
  const scrollToSection = useScrollToSection();
  const { sectionRef, dropPosition, dragHandlers } = useSectionDragAndDrop({
    sectionId: id,
    index,
    title,
    onReorder,
    draggedRef,
  });

  const clearBlockSelection = useClearBlockSelection();
  const unsetEditorSelection = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    clearBlockSelection();
  };

  return (
    <ListItem
      {...dragHandlers}
      key={id}
      disablePadding
      data-testid="section-title-textfield-input"
      ref={sectionRef}
      sx={{
        '&:hover': {
          [`& .section-${id}-drag-icon`]: {
            opacity: '1 !important',
            width: '100% !important',
          },
          [`& .section-${id}-meatball-icon`]: {
            opacity: '1 !important',
          },
        },
      }}
    >
      <Box display="flex" flexDirection="column" width="100%">
        {dropPosition === 'top' && <SectionGhost title={draggedRef.current?.title} />}

        <ListItemButton
          onClick={() => scrollToSection(id)}
          onMouseDown={unsetEditorSelection}
          sx={{
            padding: '0.5rem 0.625rem 0.5rem 0.5rem',
            borderRadius: '0rem 0.5rem 0.5rem 0rem',
            background: isActive ? COLORS.appFog800 : 'transparent',
            boxShadow: isActive
              ? '0px 2px 1px -1px rgba(0, 0, 0, 0.20), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)'
              : 'none',
            '&:hover': {
              background: isActive ? COLORS.appFog800 : 'transparent',
            },
          }}
        >
          <IcoDrag
            className={`section-${id}-drag-icon`}
            style={{
              display: 'block',
              marginRight: '0.5rem',
              cursor: 'grab',
              opacity: 0,
              maxWidth: '1em',
              width: '0%',
              transition: 'opacity 0.2s ease-in-out, width 0.8s ease-in-out',
            }}
            color={isActive ? COLORS.white : COLORS.appFog800}
          />
          <ListItemText
            data-section-id={id}
            sx={{ color: isActive ? COLORS.white : COLORS.appFog800 }}
            primary={
              <Typography
                variant="body2"
                sx={{
                  WebkitFontSmoothing: isActive ? 'subpixel-antialiased' : 'initial',
                  fontFeatureSettings: "'liga' off, 'clig' off",
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: isActive ? COLORS.white : COLORS.appFog800,
                }}
              >
                {title}
              </Typography>
            }
          />
          <SectionMenu id={id} index={index} isActive={isActive} />
        </ListItemButton>
        {dropPosition === 'bottom' && <SectionGhost title={draggedRef.current?.title} />}
      </Box>
    </ListItem>
  );
}
